import { useContext, useState } from 'react';
import { TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CustomTable from '../../../../common/Table/CustomTable/CustomTable';
import classes from './MainTable.module.scss';
import { useWindowSize } from '../../../../../utils/hooks';
import { responsiveSizeTimesheet, timesheetsStatuses } from '../../../../../utils/constants';
import { columns } from './helper';
import { addNewClaim, updateClaim } from '../../../../../actions/expense';
import { ExpenseColumnType } from './types';
import ExpenseAttachment from '../ExpenseAttachment/ExpenseAttachment';
import BaseModal from '../../../../common/ModalWindows/BaseModal';

import ExpenseCardList from '../ExpenseCardList/ExpenseCardList';

import { EditExpenseContext } from '../../../../../context/editContexts';
import { numberWithCommas } from '../../../../../utils/helpers';
import NotificationModal from '../../../../common/ModalWindows/NotificationModal';
import MainButton from '../../../../common/buttons/MainButton';
import { currencyTextFieldStyles } from '../../../CreateTimeandExpense/components/CurrencyStep/CurrencyStep';

export default function MainTable({
  expenseCurrency,
  totalClaim,
  data,
  save,
  submit,
  deleteLine,
  approve,
  reject,
  saving,
  isApprover,
  getFormData,
  placementStatus,
  placementHandler,
  saveIndividualCardMobile
}) {
  const size = useWindowSize();
  const { t } = useTranslation();
  const expensetypes = useSelector((state) => state?.constFromApi?.expensetypes);
  const currencies = useSelector((state) => state?.constFromApi?.currencies);
  const dispatch = useDispatch();
  const [showAttachment, setShowAttachment] = useState({ show: false, id: '', expense: {}, checked: false });
  const [savingFiles, setSavingFiles] = useState(false);
  const expenseDetails = data?.ExpenseDetails;
  const ctx = useContext(EditExpenseContext);
  const [showFxCurrencyChange, setShowFxCurrencyChange] = useState({ show: false, id: '' });
  const [fxRate, setFxRate] = useState(0);

  const addHandler = async () => {
    await dispatch(addNewClaim());
    ctx.setHasChanges(true);
  };

  const changeHandler = async (type, id, newValue) => {
    let expense = expenseDetails.find((claim) => claim.Id === id);
    const isSameCurrency = expenseDetails.every(
      (detail) =>
        (detail.ExpenseCurrency?.CurrencyName === detail.FxCurrency?.CurrencyName || detail.FxCurrency === null) &&
        detail?.CategoryId !== 5
    );

    switch (type) {
      case ExpenseColumnType.DATE:
        expense = {
          ...expense,
          Date: newValue
        };
        break;
      case ExpenseColumnType.CATEGORY:
        expense = {
          ...expense,
          CategoryId: newValue,
          Category: {
            Name: expensetypes?.find((expensetype) => expensetype?.value === newValue)?.label || 'Travel'
          }
        };
        break;
      case ExpenseColumnType.EXPENSE_AMOUNT:
        expense = {
          ...expense,
          ExpenseAmount: newValue || 0,
          TotalAmount: expense.FxAmount > 0 ? newValue * expense.FxAmount || 0 : newValue || 0
        };
        break;
      case ExpenseColumnType.FX_CURRENCY:
        if (expense.ExpenseCurrency.CurrencyName !== newValue.label && isSameCurrency) {
          setShowFxCurrencyChange({ show: true, id });
        }
        expense = {
          ...expense,
          FxCurrencyId: newValue.key,
          FxCurrency: {
            CurrencyName: newValue.label
          },
          FxAmount: 1,
          TotalAmount: 1 * expense.ExpenseAmount
        };
        break;
      case ExpenseColumnType.RATE:
        expense = {
          ...expense,
          FxAmount: newValue || 0,
          TotalAmount: (newValue || 0) * expense.ExpenseAmount
        };
        break;
      case ExpenseColumnType.CODE:
        expense = {
          ...expense,
          CostCode: newValue
        };
        break;
      case ExpenseColumnType.COMMENT:
        expense = {
          ...expense,
          Comment: newValue
        };
        break;
      case ExpenseColumnType.RECEIPT:
        if (!newValue) {
          setShowAttachment({ show: !showAttachment.show, id, expense });
        }
        expense = {
          ...expense,
          NoReceiptCheck: newValue
        };
        dispatch(updateClaim(expense));
        if (newValue) {
          ctx.setHasChanges(true);
        } else {
          ctx.setHasChanges(false);
        }
        return;
      case ExpenseColumnType.RECEIPT_DELETE:
        expense = {
          ...expense,
          ExpenseAttachments: expense?.ExpenseAttachments?.filter(
            (file) => JSON.stringify(file) !== JSON.stringify(newValue)
          )
        };
        setShowAttachment({ show: true, id, expense, checked: newValue?.checked });

        break;
      case ExpenseColumnType.RECEIPT_SAVE: {
        const formData = new FormData();
        expense = {
          ...expense,
          attachments: !newValue.checked ? newValue.files : [],
          ExpenseAttachments: !newValue.checked ? [...expense.ExpenseAttachments, ...newValue.files] : [],
          AddedNow: !newValue.checked,
          NoReceiptCheck: newValue.checked
        };
        if (expense?.Type === 'New') {
          setShowAttachment({ show: false, id: '', expense, checked: newValue?.checked });
          break;
        }
        newValue?.files?.map((file) => {
          formData.append('attachments', file);
          return formData;
        });

        if (!newValue.checked && newValue?.files?.length) {
          setSavingFiles(true);
          // try {
          //   const result = await tneAxiosClient.post(`v1/expense/attachment/${id}`, formData);
          //   expense = {
          //     ...expense,
          //     ExpenseAttachments: result?.data?.Data?.Data
          //   };
          // } catch (e) {
          //   basicErrorHandler(dispatch, e);
          // }
          setSavingFiles(false);
        }
        setShowAttachment({ show: false, id: '', expense, checked: newValue.checked });
        expense = {
          ...expense,
          attachments: []
        };
        dispatch(updateClaim(expense));
        ctx.setHasChanges(true);
        break;
      }
      default:
        break;
    }

    dispatch(updateClaim(expense));
    ctx.setHasChanges(true);
  };

  const handleCloseAttachment = () => {
    setShowAttachment({ show: false, id: '' });
  };

  return (
    <div className={classes.maintable}>
      {size.width > responsiveSizeTimesheet ? (
        <CustomTable
          data={data?.ExpenseDetails || []}
          columns={() =>
            columns(
              t,
              expensetypes,
              addHandler,
              changeHandler,
              deleteLine,
              ctx.isEdit,
              currencies,
              `${data.User.FirstName} ${data.User.LastName}`,
              data
            )
          }
          withFooter
          withLines={{
            header: false,
            body: false,
            footer: false
          }}
          footerMargin="0"
          bodyWithBorderRadius={false}
          withEmbedded
          bodyStyles={{ padding: '20px 26px 10px', height: 'calc(100% + 30px)' }}
          footerStyles={{ padding: '0px 26px 80px' }}
          embedContent={`${expenseCurrency || ''} ${numberWithCommas(parseFloat(totalClaim || 0).toFixed(2)) || 0} `}
        />
      ) : (
        <ExpenseCardList
          data={data}
          addHandler={addHandler}
          changeHandler={changeHandler}
          saveHandler={save}
          submitHandler={submit}
          totalClaim={totalClaim}
          expenseCurrency={expenseCurrency}
          deleteLineHandler={deleteLine}
          approveHandler={approve}
          rejectHandler={reject}
          saving={saving}
          saveIndividualCardMobile={saveIndividualCardMobile}
          editable={
            ![timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(
              data?.Status?.Status
            ) && !isApprover
          }
          getFormData={(dt) => getFormData(dt)}
          placementStatus={placementStatus}
          placementHandler={placementHandler}
        />
      )}

      <BaseModal
        open={showAttachment.show}
        fullscreen={false}
        closeHandler={() => setShowAttachment(false)}
        title="&nbsp;"
        hideFooter
        hideHeader
        withSeparator={false}
        minWidth="550px"
        paperStyles={{
          maxWidth: '660px'
        }}
      >
        <ExpenseAttachment
          data={showAttachment.expense}
          handleCloseAttachment={handleCloseAttachment}
          open={showAttachment.show}
          saveAttachment={(checked, files) =>
            changeHandler(ExpenseColumnType.RECEIPT_SAVE, showAttachment.id, { files, checked })
          }
          isEdit={ctx?.isEdit}
          deleteAttachment={(id) => changeHandler(ExpenseColumnType.RECEIPT_DELETE, showAttachment.id, id)}
          savingFiles={savingFiles}
          isApprover={isApprover}
        />
      </BaseModal>
      <NotificationModal
        open={showFxCurrencyChange.show}
        onClose={() => setShowFxCurrencyChange({ show: false })}
        paddingX={size.width > responsiveSizeTimesheet ? '36px' : '49px'}
        height={size.width > responsiveSizeTimesheet ? '450px' : '600px'}
        gap="44px"
      >
        <p className={classes.expenseCurrencyText}>{t('dashboard.createclaim.steps.currencychange')}</p>
        <div className={classes.filedsContainer}>
          <TextField
            placeholder={t('dashboard.createclaim.steps.rate')}
            value={fxRate}
            type="number"
            onChange={(e) => {
              setFxRate(parseFloat(e.target.value));
            }}
            sx={currencyTextFieldStyles('125px', '10px 0 0 10px', '0 10px 0 0')}
          />
          <span className={classes.expenseCurrencyText}>{expenseDetails[0].ExpenseCurrency.CurrencyName}</span>
        </div>
        <div className={classes.buttonContainer}>
          <MainButton
            rounded
            size="small"
            forceWidth={size.width > responsiveSizeTimesheet ? '204px' : '100%'}
            action={() => {
              changeHandler(ExpenseColumnType.RATE, showFxCurrencyChange.id, fxRate);
              setShowFxCurrencyChange({ show: false });
            }}
          >
            {t('common.general.save')}
          </MainButton>
        </div>
      </NotificationModal>
    </div>
  );
}
